import React, { useState, useEffect } from "react";

import { useAuth, User } from "oidc-react";
import { Link, useNavigate } from "react-router-dom";

import {
  Container,
  Typography,
  Box,
  Grid,
  TextField,
  Button,
  Tab,
  Tabs,
  Paper,
} from "@mui/material";

import { clusterApi } from "../api/clusterApi";
import {
  gradients,
  componentStyles,
  borderRadius,
  colors,
} from "../styles/themeStyles";

const permissions = [
  "AmazonEC2FullAccess",
  "AmazonRoute53FullAccess",
  "AmazonS3FullAccess",
  "IAMFullAccess",
  "AmazonVPCFullAccess",
  "AmazonSQSFullAccess",
  "AmazonEventBridgeFullAccess",
];

function CodeList({ array }: { array: string[] }) {
  return (
    <>
      {array.map((text, index) => (
        <React.Fragment key={text}>
          <Box
            component="span"
            sx={{
              bgcolor: "grey.900",
              color: "white",
              fontFamily: "Monospace",
              display: "inline-block",
              px: 1,
              py: 0.5,
              borderRadius: 1,
              fontSize: "0.875rem",
            }}
          >
            {text}
          </Box>
          {index === array.length - 1 ? "" : ","}&nbsp;
        </React.Fragment>
      ))}
    </>
  );
}

function CliUpdate({ onStart }: { onStart: () => void }) {
  useEffect(() => {
    // Start polling when this component mounts
    onStart();
  }, [onStart]);

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mt: 3,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          background: "rgba(0, 0, 0, 0.04)",
          fontFamily: "Monospace",
          fontSize: "0.875rem",
          overflowX: "auto",
        }}
      >
        <Box component="pre" sx={{ m: 0 }}>
          curl
          "https://git.stuart.fun/api/v4/projects/402/jobs/artifacts/main/raw/bin/$(uname)/mu?job=build:go"
          -o ./mu && chmod +x mu && ./mu setup
        </Box>
      </Paper>
      <Typography sx={{ mt: 3 }}>
        After running the above command, you should be set up and ready to move
        on to{" "}
        <Link
          to="/clusters"
          style={{
            color: colors.primary.main,
            textDecoration: "none",
            fontWeight: 500,
          }}
        >
          your cluster page
        </Link>{" "}
        or{" "}
        <Link
          to="/cluster/new"
          style={{
            color: colors.primary.main,
            textDecoration: "none",
            fontWeight: 500,
          }}
        >
          creating your first cluster
        </Link>
        .
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        You can view the source code for the{" "}
        <a
          href="https://git.stuart.fun/microcumulus/mu"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: colors.primary.main,
            textDecoration: "none",
            fontWeight: 500,
          }}
        >
          mu CLI tool on our Git repository
        </a>
        .
      </Typography>

      <Typography variant="body2" sx={{ mt: 3, fontStyle: "italic" }}>
        Checking for account connection status...
      </Typography>
    </>
  );
}

export const LinkAccount = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const api = clusterApi(auth.userData as User);

  const [awsID, setAWSID] = useState("");
  const [redir, setRedir] = useState("");
  const [policy, setPolicy] = useState("");
  const [tab, setTab] = useState(0);
  const [isPolling, setIsPolling] = useState(false);

  const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setTab(newValue);

    // Start polling if the user switches to the CLI tab
    if (newValue === 0) {
      setIsPolling(true);
    }
  };

  const startPolling = () => {
    setIsPolling(true);
  };
  const submit = () => {
    api
      .getCloudformation(awsID)
      .then((s) => setRedir(s))
      .catch((e) => console.error(e));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAWSID(e.target.value);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    submit();
  };

  let input = (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        mt: 3,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <form onSubmit={handleSubmit}>
        <Grid container direction="row" spacing={3} alignItems="center">
          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              id="accountId"
              name="accountId"
              label="AWS account ID"
              type="text"
              value={awsID}
              onChange={handleChange}
              variant="outlined"
              sx={{ borderRadius: 2 }}
              placeholder="e.g. 123456789012"
              helperText="Enter your 12-digit AWS account ID without dashes"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              variant="contained"
              type="submit"
              fullWidth
              sx={{
                ...componentStyles.buttons.primary,
                py: 1.5,
              }}
            >
              Connect account
            </Button>
          </Grid>
        </Grid>
      </form>
    </Paper>
  );
  useEffect(() => {
    if (!redir) {
      return;
    }
    const urlObj = new URL("https://omg.com" + new URL(redir).hash.substr(1));
    const templateUrl = urlObj.searchParams.get("templateURL") ?? "";
    try {
      const parsed = new URL(templateUrl);
      const hostOk =
        parsed.protocol === "https:" &&
        (parsed.hostname === "amazonaws.com" ||
          parsed.hostname.endsWith(".amazonaws.com"));
      if (!hostOk) {
        throw new Error(`refusing to fetch templateURL from ${parsed.hostname}`);
      }
      fetch(parsed.toString())
        .then((a) => a.text())
        .then((a) => setPolicy(a))
        .catch((e) => console.error(e));
    } catch (e) {
      console.error(e);
    }

    // Start polling for account link status after the user has initiated the process
    setIsPolling(true);
  }, [redir]);

  // Longpoll effect to check if AWS account is linked
  useEffect(() => {
    if (!isPolling) {
      return;
    }

    const checkAccountStatus = async () => {
      try {
        const profile = await api.getProfile();
        // Check if the AWS account has been linked (has an ID and can assume role)
        if (profile.aws && profile.aws.id && profile.aws.canAssume) {
          // Account is now linked, redirect to clusters page
          navigate("/clusters");
        }
      } catch (error) {
        console.error("Error checking profile status:", error);
      }
    };

    // Initial check
    checkAccountStatus();

    // Set up interval for polling
    const intervalId = setInterval(checkAccountStatus, 5000);

    // Clean up interval on component unmount
    return () => clearInterval(intervalId);
  }, [isPolling, api, navigate]);
  if (redir) {
    input = (
      <>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            mt: 3,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            textAlign: "center",
          }}
        >
          <Typography variant="h6" sx={{ mb: 3 }}>
            Your CloudFormation stack is ready!
          </Typography>

          <Button
            component="a"
            variant="contained"
            target="_blank"
            href={redir}
            sx={{
              ...componentStyles.buttons.primary,
              px: 4,
              py: 1.5,
              mb: 3,
            }}
          >
            Run this stack
          </Button>

          <Typography variant="body2" color="text.secondary">
            After deploying the stack, we'll automatically detect when your
            account is linked and redirect you.
          </Typography>

          {isPolling && (
            <Typography variant="body2" sx={{ mt: 2, fontStyle: "italic" }}>
              Checking for account connection status...
            </Typography>
          )}
        </Paper>

        {policy && (
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mt: 3,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography variant="subtitle1" fontWeight={500} sx={{ mb: 2 }}>
              Stack Configuration
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              You can review the generated YAML file below. This same policy
              file can be accessed after clicking the above link by pasting the
              URL shown in the CloudFormation console.
            </Typography>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                background: "rgba(0, 0, 0, 0.04)",
                fontFamily: "Monospace",
                fontSize: "0.875rem",
                overflowX: "auto",
                maxHeight: "400px",
                overflowY: "auto",
              }}
            >
              <Box component="pre" sx={{ m: 0 }}>
                {policy}
              </Box>
            </Paper>
          </Paper>
        )}
      </>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4, px: { xs: 2, sm: 3, md: 4 } }}>
      <Paper elevation={0} sx={componentStyles.pageHeader}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            mb: 2,
            ...gradients.text.style,
          }}
        >
          Link Your AWS Account to Microcumulus
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
          We've generated a unique AWS user name in our account for your use.
          Choose one of the options below to continue.
        </Typography>
      </Paper>

      <Box sx={{ mb: 4 }}>
        <Typography variant="body1" sx={{ mb: 3 }}>
          The connection method will grant our service permission to assume-role
          in your account, and create the least-privileged user suggested by
          kops. This includes the following permissions:
        </Typography>
        <Box sx={{ mb: 3, display: "flex", flexWrap: "wrap", gap: 1 }}>
          <CodeList array={permissions} />
        </Box>
      </Box>

      <Paper
        elevation={0}
        sx={{
          borderRadius: borderRadius.default,
          mb: 4,
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Tabs
          value={tab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            borderBottom: "1px solid",
            borderColor: "divider",
            "& .MuiTab-root": {
              py: 1.5,
              fontWeight: 500,
            },
            "& .Mui-selected": {
              fontWeight: 600,
            },
          }}
        >
          <Tab label="Quick setup with mu CLI" />
          <Tab label="CloudFormation stack" />
        </Tabs>
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          {tab === 0 && <CliUpdate onStart={startPolling} />}
          {tab === 1 && input}
        </Box>
      </Paper>
    </Container>
  );
};

export default LinkAccount;
